/* eslint-disable no-nested-ternary */
import { useCallback, useContext, useEffect } from "react"
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Heading,
  ModalProps,
} from "@chakra-ui/react"
import { Block, Button, Close, useStyletron } from "@mezo-org/mezo-clay"
import { useGetCurrentAccount, useSignOut } from "@mezo-org/passport"
import { Connector, useConfig } from "wagmi"

import ledger from "#/assets/ledger.svg"
import {
  selectAccountCreationStatus,
  selectIsConnected,
  selectOnboardingFlow,
  setAccountCreationStatus,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import ModalContainer from "#/shared/components/ModalContainer"
import { ENABLE_LEDGER_WALLET_PLACEHOLDER } from "#/shared/constants"
import { FlowContext } from "#/shared/context"
import {
  useConnectWalletMezoAccount,
  useConnectWalletModal,
  useWalletConnectors,
} from "#/shared/hooks"
import {
  AccountCreationStatus,
  ExtendedConector,
  OnboardingFlow,
} from "#/shared/types"
import { getConnectorIcon } from "#/shared/utils"

import ConnectWalletButton from "./ConnectWalletButton"
import ConnectWalletModalSwitchFlow from "./ConnectWalletModalSwitchFlow"

type PlaceholderConnector = Pick<ExtendedConector, "id" | "name"> & {
  icon: string
}

const placeholderBitcoinConnectors: PlaceholderConnector[] = [
  ENABLE_LEDGER_WALLET_PLACEHOLDER && {
    id: "ledger-placeholder",
    name: "Ledger",
    icon: ledger,
  },
].filter(Boolean) as PlaceholderConnector[]

export default function ConnectWalletModal({
  isOpen,
  onClose,
}: Pick<ModalProps, "isOpen" | "onClose">) {
  const isConnected = useDappSelector(selectIsConnected)
  const accountCreationStatus = useDappSelector(selectAccountCreationStatus)
  const onboardingFlow = useDappSelector(selectOnboardingFlow)

  const dispatch = useDappDispatch()

  const { connectors, isMaybeRejected } = useWalletConnectors()
  const { signInWithWallet } = useConnectWalletMezoAccount()

  const [css, theme] = useStyletron()

  const {
    connectorToReconnect,
    hasConnectionError,
    isUnsupportedAddressType,
    isOnWrongNetwork,
    pendingConnectionId,
    isBackgroundModal,
    onConnect,
    onCloseModal,
  } = useConnectWalletModal(onClose)

  const { isBitcoinFlow } = useContext(FlowContext)

  const { data: currentAccount } = useGetCurrentAccount()
  const { signOutAsync } = useSignOut()

  const { storage } = useConfig()

  const onConnectHandler = useCallback(
    async (connector: Connector) => {
      // Wallet already connected but signing message failed, let's allow to resume by clicking
      // on the connector button again
      if (accountCreationStatus === AccountCreationStatus.NotSigned) {
        const [address] = await connector.getAccounts()
        dispatch(setAccountCreationStatus(AccountCreationStatus.Signing))

        // If we are in Sign in flow we prompt user to sign message
        if (onboardingFlow === OnboardingFlow.SIGN_IN) {
          await signInWithWallet(address)
        }
      } else {
        // Fix Xverse not reconnecting on error by forcing disconnect if
        // hasConnectionError is true.
        // To reproduce: Remove this if, connect Xverse with wrong network to a
        // dapp (e.g. Mainnet Xverse to Sepolia dapp) - the error should be
        // displayed. Change network in Xverse to the same as in the dapp and
        // click `try again` - error still persists. Thanks to this if statement
        // the last error won't happen.
        if (hasConnectionError && connector.id === "orangekit-xverse") {
          await connector.disconnect()
        }

        const recentConnectorId = await storage?.getItem("recentConnectorId")
        // When refreshing the dApp while connected to a Bitcoin account, we're
        // still connected on the passport side but not on the dApp side due to
        // missing auto-connect logic, so we must sign out the old connector
        // before connecting a new one to avoid errors.
        if (currentAccount && connector.id !== recentConnectorId) {
          await signOutAsync()
        }
        // Wallet not connected yet, let's connect it and look for the account
        onConnect(connector)
      }
    },
    [
      hasConnectionError,
      accountCreationStatus,
      signInWithWallet,
      dispatch,
      onConnect,
      onboardingFlow,
      currentAccount,
      signOutAsync,
      storage,
    ],
  )

  useEffect(() => {
    // After wallet is connected it will go to either:
    // - Created state - if account already exists and signing SIWW established
    //   a connection with a server for existing account
    // - Idle state - if account doesn't exist and user needs to enter valid
    //   referral code to create an account
    // In both cases we want to close the modal and display the account menu
    if (
      isOpen &&
      isConnected &&
      (accountCreationStatus === AccountCreationStatus.Idle ||
        accountCreationStatus === AccountCreationStatus.Created)
    ) {
      onCloseModal()
    }
  }, [dispatch, isConnected, accountCreationStatus, onCloseModal, isOpen])

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={onCloseModal}
      isBackgroundModal={isBackgroundModal}
      closeButtonIcon=""
    >
      <Box
        pt="24px"
        pb={2}
        w="522px"
        rounded="16px"
        bg="primary.white.100"
        my={5}
      >
        <Box pl={10} pr={5}>
          <Block
            className={css({
              marginBottom: theme.sizing.scale600,
              display: "flex",
              width: "100%",
              justifyContent: "flex-end",
            })}
          >
            <Button
              kind="secondary"
              shape="pill"
              size="small"
              onClick={onCloseModal}
              overrides={{
                BaseButton: {
                  style: () => ({ padding: theme.sizing.scale400 }),
                },
              }}
            >
              <Close color={theme.colors.black} size={theme.sizing.scale600} />
            </Button>
          </Block>
          <Heading variant="h3" mb={8}>
            Sign in with {isBitcoinFlow ? "Bitcoin" : "Ethereum"}
          </Heading>
        </Box>
        {hasConnectionError && connectorToReconnect && (
          <>
            <Box>
              <Box px={5}>
                <Alert status="error" mb={4}>
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Error!</AlertTitle>
                    <AlertDescription>
                      {isUnsupportedAddressType
                        ? "Only Native Segwit, Nested Segwit or Legacy addresses supported at this time, please try a different address or another wallet."
                        : isOnWrongNetwork
                          ? "Wrong network detected in your wallet. Please choose proper network and try again."
                          : "Something went wrong. Try again."}
                    </AlertDescription>
                  </Box>
                </Alert>
              </Box>
            </Box>
            <ConnectWalletButton
              key={connectorToReconnect.id}
              label={connectorToReconnect.name}
              icon={getConnectorIcon(connectorToReconnect)}
              connectionRejected
              shouldShowStatus={pendingConnectionId === connectorToReconnect.id}
              onClick={() => {
                onConnectHandler(connectorToReconnect)
              }}
              analyticsId={connectorToReconnect.analyticsId}
            />
          </>
        )}
        {!hasConnectionError && (
          <>
            {(connectors as ExtendedConector[]).map((connector) => (
              <ConnectWalletButton
                key={connector.id}
                label={connector.name}
                icon={getConnectorIcon(connector)}
                connectionRejected={isMaybeRejected}
                shouldShowStatus={pendingConnectionId === connector.id}
                onClick={() => {
                  onConnectHandler(connector)
                }}
                analyticsId={connector.analyticsId}
              />
            ))}

            {isBitcoinFlow &&
              placeholderBitcoinConnectors.map((connector) => (
                <ConnectWalletButton
                  variant="coming-soon"
                  key={connector.id}
                  label={connector.name}
                  icon={connector.icon}
                />
              ))}

            <ConnectWalletModalSwitchFlow />
          </>
        )}
      </Box>
    </ModalContainer>
  )
}
